import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/StatusBanner";
import { PaginationBar } from "@/components/PaginationBar";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";

export default function TenantsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "";
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 350);
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [debouncedQ, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit: 20 };
    if (debouncedQ) params.q = debouncedQ;
    if (status) params.status = status;
    api.get("/platform/tenants", { params }).then((res) => {
      if (!cancelled) setData(res.data);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedQ, status]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div>
          <div className="label-overline">Truck Stops</div>
          <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">Tenants</h1>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="tenants-search"
            placeholder="Search name or slug…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full sm:w-64 rounded h-10 font-mono"
          />
          <Link to="/platform/tenants/new">
            <Button data-testid="tenants-new-btn" className="rounded bg-blue-700 hover:bg-blue-800 whitespace-nowrap">
              + New tenant
            </Button>
          </Link>
        </div>
      </div>

      {status && (
        <div className="mb-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs">
          <span className="label-overline text-blue-800">Filter</span>
          <span className="font-mono">status = {status}</span>
          <button data-testid="tenants-clear-status"
                  onClick={() => setSearchParams({})}
                  className="text-blue-700 hover:underline font-semibold">Clear</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Business</th>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Cycle</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                No tenants match — {debouncedQ ? "try a different search." : "create your first truck stop."}
              </td></tr>
            )}
            {data.items.map((t) => (
              <tr key={t.id} className="border-b border-slate-100" data-testid={`tenant-row-${t.slug}`}>
                <td className="px-4 py-3 font-semibold">{t.business_name}</td>
                <td className="px-4 py-3 font-mono text-slate-600">{t.slug}</td>
                <td className="px-4 py-3">{t.plan === "financial" ? "Financial" : "Management"}</td>
                <td className="px-4 py-3 font-mono">{t.billing_cycle}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtMoney(t.price_cents - (t.discount_cents || 0))}</td>
                <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3">
                  <Link to={`/platform/tenants/${t.id}`}
                        data-testid={`tenant-open-${t.slug}`}
                        className="text-blue-700 hover:text-blue-800 font-semibold">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar page={data.page || 1} pages={data.pages || 1} onChange={setPage} />
      </div>
    </div>
  );
}
